import { useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ReplayIcon from '@mui/icons-material/Replay';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useTranslation } from 'react-i18next';
import type { Goal, GoalTask, GoalStatus } from '../../api/types';
import { GoalTaskTable } from './GoalTaskTable';
import { GoalDeadlineChip } from './GoalDeadlineChip';
import { GoalPriorityChip } from './GoalPriorityChip';

const STATUS_CHIP_COLOR: Record<Exclude<GoalStatus, 'active'>, 'success' | 'default'> = {
  done: 'success',
  dropped: 'default',
};

interface Props {
  goal: Goal;
  /** False for a follower reading someone else's garage. */
  canEdit: boolean;
  onEdit: () => void;
  /** Change priority / deadline in place from their chips. */
  onUpdate: (changes: Partial<Pick<Goal, 'priority' | 'deadline'>>) => void;
  onOpenPhotos: () => void;
  onStatusChange: (status: GoalStatus) => void;
  onDelete: () => void;
  onAddTask: () => void;
  onToggleTask: (task: GoalTask, completed: boolean) => void;
  onEditTask: (task: GoalTask) => void;
  onDeleteTask: (task: GoalTask) => void;
}

/**
 * One goal: cover photo, title, priority, description and its task list — parts to buy
 * and jobs to do, each with an optional estimated price and a tick-off box — and,
 * once anything is priced, what it all adds up to.
 */
export function GoalCard({
  goal,
  canEdit,
  onEdit,
  onUpdate,
  onOpenPhotos,
  onStatusChange,
  onDelete,
  onAddTask,
  onToggleTask,
  onEditTask,
  onDeleteTask,
}: Props) {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const completedCount = goal.tasks.filter((task) => task.status === 'completed').length;
  const cover = goal.photos[0];

  const closeMenuAnd = (action: () => void) => () => {
    setMenuAnchor(null);
    action();
  };

  return (
    <Card variant="outlined">
      {cover && (
        <CardActionArea
          onClick={onOpenPhotos}
          aria-label={t('garageCar.goals.photos.open', { count: goal.photos.length })}
        >
          <CardMedia component="img" image={cover.url} alt={goal.title} sx={{ height: 180 }} />
        </CardActionArea>
      )}
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h6">{goal.title}</Typography>
                <GoalPriorityChip
                  priority={goal.priority}
                  onChange={canEdit ? (priority) => onUpdate({ priority }) : undefined}
                />
                <GoalDeadlineChip
                  goal={goal}
                  onChange={canEdit ? (deadline) => onUpdate({ deadline }) : undefined}
                />
                {goal.status !== 'active' && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={STATUS_CHIP_COLOR[goal.status]}
                    label={t(`garageCar.goals.status.${goal.status}`)}
                  />
                )}
              </Stack>
              {goal.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {goal.description}
                </Typography>
              )}
            </Box>
            {canEdit && (
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                aria-label={t('garageCar.goals.actions.more')}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('garageCar.goals.tasks.count', { count: goal.tasks.length })}
              {completedCount > 0 &&
                ` · ${t('garageCar.goals.tasks.completedCount', { count: completedCount })}`}
            </Typography>

            {(goal.tasks.length > 0 || (canEdit && goal.status === 'active')) && (
              <GoalTaskTable
                goal={goal}
                canEdit={canEdit}
                onToggleTask={onToggleTask}
                onEditTask={onEditTask}
                onDeleteTask={onDeleteTask}
                onAddTask={canEdit && goal.status === 'active' ? onAddTask : undefined}
              />
            )}
          </Stack>
        </Stack>
      </CardContent>

      <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={closeMenuAnd(onEdit)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('garageCar.goals.actions.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={closeMenuAnd(onOpenPhotos)}>
          <ListItemIcon>
            <PhotoLibraryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t('garageCar.goals.actions.photos', { count: goal.photos.length })}
          </ListItemText>
        </MenuItem>
        {goal.status === 'active' && (
          <MenuItem onClick={closeMenuAnd(() => onStatusChange('done'))}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('garageCar.goals.actions.markDone')}</ListItemText>
          </MenuItem>
        )}
        {goal.status === 'active' && (
          <MenuItem onClick={closeMenuAnd(() => onStatusChange('dropped'))}>
            <ListItemIcon>
              <BlockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('garageCar.goals.actions.drop')}</ListItemText>
          </MenuItem>
        )}
        {goal.status !== 'active' && (
          <MenuItem onClick={closeMenuAnd(() => onStatusChange('active'))}>
            <ListItemIcon>
              <ReplayIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('garageCar.goals.actions.reopen')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={closeMenuAnd(onDelete)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('garageCar.goals.actions.delete')}</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
}
